/**
 * Renderizadores compartilhados das peças (mapa do caso + estrutura da petição).
 * Usados pelos viewers isolados (ModalMapaViewer/ModalBlocosViewer) e pelo pane
 * esquerdo do split de refino (RefinoSplit).
 *
 * PALETA (legibilidade WCAG):
 *  • Claro: superfície "papel" — texto slate-800/900, títulos azul-escuro.
 *  • Escuro: fundo escuro (definido pelo container) — texto BRANCO (slate-100) e
 *    DESTAQUES (títulos, negrito) em ÂMBAR para alto contraste.
 */
import type { ReactNode } from 'react';

// ── Mapa (markdown → React estilizado) ──────────────────────────────────────

function renderizarInline(text: string) {
  const parts = text.split('**');
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold text-blue-900 dark:text-amber-300">{part}</strong>
    ) : (
      part
    ),
  );
}

export function MapaMarkdown({ conteudo }: { conteudo: string }) {
  if (!conteudo) return null;
  const lines = conteudo.split('\n');
  const out: ReactNode[] = [];
  lines.forEach((line, idx) => {
    const t = line.trim();
    if (t.startsWith('# ')) {
      out.push(
        <h1 key={idx} className="text-xl sm:text-2xl font-display font-bold text-blue-900 dark:text-amber-300 mt-6 mb-4 border-b border-slate-300 dark:border-slate-700 pb-2">
          {t.slice(2)}
        </h1>,
      );
    } else if (t.startsWith('## ')) {
      out.push(
        <h2 key={idx} className="text-lg sm:text-xl font-display font-bold text-blue-800 dark:text-amber-200 mt-5 mb-3">
          {t.slice(3)}
        </h2>,
      );
    } else if (t.startsWith('### ')) {
      out.push(
        <h3 key={idx} className="text-base sm:text-lg font-display font-bold text-slate-800 dark:text-amber-100 mt-4 mb-2">
          {t.slice(4)}
        </h3>,
      );
    } else if (t.startsWith('- ') || t.startsWith('* ')) {
      out.push(
        <li key={idx} className="ml-5 list-disc pl-1 text-sm text-slate-800 dark:text-slate-100 my-1 font-sans">
          {renderizarInline(t.slice(2))}
        </li>,
      );
    } else if (t.startsWith('> ')) {
      out.push(
        <blockquote key={idx} className="border-l-4 border-blue-500 dark:border-amber-400 bg-blue-500/5 dark:bg-amber-400/10 pl-3 py-2 my-4 text-sm text-slate-800 dark:text-slate-100 rounded-r-lg font-sans">
          {renderizarInline(t.slice(2))}
        </blockquote>,
      );
    } else if (t.startsWith('|')) {
      // Linha de tabela markdown — mostra monoespaçada legível (sem montar <table>)
      out.push(
        <div key={idx} className="font-mono text-xs sm:text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap leading-relaxed">
          {t}
        </div>,
      );
    } else if (t === '') {
      out.push(<div key={idx} className="h-2" />);
    } else {
      out.push(
        <p key={idx} className="text-sm leading-relaxed text-slate-800 dark:text-slate-100 my-2 font-sans">
          {renderizarInline(t)}
        </p>,
      );
    }
  });
  return <div className="max-w-3xl mx-auto space-y-1">{out}</div>;
}

// ── Blocos / Estrutura da petição (lista tipada → cartões) ───────────────────

const COR_BLOCO: Record<string, string> = {
  ENDERECO:   'bg-slate-500/15 text-slate-700 dark:text-slate-200 border-slate-500/30',
  H1:         'bg-blue-500/15 text-blue-700 dark:text-amber-300 border-blue-500/30',
  H2:         'bg-indigo-500/15 text-indigo-700 dark:text-amber-200 border-indigo-500/30',
  TOPICO:     'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30',
  P:          'bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/20',
  CITACAO:    'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
  PEDIDO:     'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  ASSINATURA: 'bg-pink-500/15 text-pink-700 dark:text-pink-300 border-pink-500/30',
  BLANK:      'bg-slate-400/10 text-slate-400 border-slate-400/20',
  PAGE_BREAK: 'bg-slate-400/10 text-slate-400 border-slate-400/20',
  TABELA_MODULO: 'bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/30',
};

function tipoDoBloco(b: any): string {
  if (b && typeof b === 'object') return String(b.tipo ?? b.type ?? b.tag ?? 'P').toUpperCase();
  return 'P';
}
function textoDoBloco(b: any): string {
  if (b == null) return '';
  if (typeof b === 'string') return b;
  // TOPICO/H1/H2/H3 guardam o conteúdo em `titulo` (+ numero/subtitulo), não em `text`.
  const tipo = String(b.tipo ?? b.type ?? b.tag ?? '').toUpperCase();
  if (tipo === 'TOPICO' || tipo === 'H1' || tipo === 'H2' || tipo === 'H3' ||
      tipo === 'CAIXA_TITULO' || tipo === 'CAIXA_SUB') {
    const titulo = b.titulo ?? b.title ?? b.text ?? b.texto ?? '';
    // `num` = rótulo hierárquico determinístico do backend (já vem com ponto:
    // '1.', '1.1.', '1.1.1.'); renumerar() apaga o legado `numero`. Fallback ao
    // `numero` (inteiro, sem ponto) só para blocos antigos ainda não renumerados.
    const num = b.num ?? b.numero ?? b.number;
    const prefixo = num != null && String(num) !== ''
      ? (/[.)]$/.test(String(num).trim()) ? `${String(num).trim()} ` : `${num}. `)
      : '';
    const sub = b.subtitulo ?? b.subtitle;
    const cabec = prefixo + String(titulo);
    return [cabec.trim(), sub ? String(sub) : ''].filter(Boolean).join(' — ');
  }
  if (tipo === 'ASSINATURA') {
    return [b.data, b.nome, b.oab].filter(Boolean).map(String).join(' · ');
  }
  if (tipo === 'MACRO' || tipo === 'MODULO') {
    return `(módulo: ${b.name ?? b.nome ?? '—'})`;
  }
  if (tipo === 'TABELA_MODULO') {
    return `🗂️ tabela/figura do modelo (${b.modulo ?? '—'} #${b.idx ?? '?'}) — inserida no documento final`;
  }
  return String(b.texto ?? b.conteudo ?? b.text ?? b.valor ?? '');
}
function classeTexto(tipo: string): string {
  if (tipo === 'H1') return 'text-base sm:text-lg font-display font-bold text-blue-900 dark:text-amber-300';
  if (tipo === 'H2' || tipo === 'TOPICO') return 'text-sm sm:text-base font-semibold text-blue-800 dark:text-amber-200';
  if (tipo === 'CITACAO') return 'text-sm italic text-slate-700 dark:text-slate-100 border-l-2 border-amber-400/60 pl-3';
  if (tipo === 'PEDIDO') return 'text-sm text-slate-900 dark:text-slate-50 font-medium';
  if (tipo === 'ENDERECO' || tipo === 'ASSINATURA') return 'text-xs text-slate-700 dark:text-slate-200 font-mono whitespace-pre-wrap';
  return 'text-sm text-slate-800 dark:text-slate-100';
}

export function BlocosLista({ blocos, destacar }: { blocos: any[]; destacar?: Set<number> }) {
  if (!Array.isArray(blocos) || blocos.length === 0) {
    return <p className="text-center text-slate-500 dark:text-slate-400 text-sm italic py-8">— ainda sem blocos —</p>;
  }
  // Numeração cardinal: incrementa só em blocos P (parágrafos do corpo).
  // O usuário usa esses números para referenciar no terminal de refino
  // (ex.: "inclua as jurisprudências selecionadas entre 4 e 5").
  let pCount = 0;
  const pIndexPorPos: Record<number, number> = {};
  blocos.forEach((b, i) => {
    if (tipoDoBloco(b) === 'P') {
      pCount += 1;
      pIndexPorPos[i] = pCount;
    }
  });
  return (
    <div className="max-w-3xl mx-auto space-y-2">
      {blocos.map((b, i) => {
        const tipo = tipoDoBloco(b);
        const texto = textoDoBloco(b);
        const realce = destacar?.has(i) ? ' bloco-flash' : '';
        const rotulo = tipo === 'P' ? String(pIndexPorPos[i]) : tipo;
        if (tipo === 'PAGE_BREAK') {
          return (
            <div key={i} className="flex items-center gap-2 py-2 text-[10px] uppercase tracking-wider text-slate-400">
              <div className="flex-1 border-t border-dashed border-slate-300 dark:border-slate-700" />
              <span>quebra de página</span>
              <div className="flex-1 border-t border-dashed border-slate-300 dark:border-slate-700" />
            </div>
          );
        }
        if (tipo === 'BLANK') return <div key={i} className={`h-3${realce} rounded`} />;
        return (
          <div
            key={i}
            data-bloco-idx={i}
            className={`flex gap-3 items-start p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/70${realce}`}
          >
            <span
              className={`shrink-0 mt-0.5 ${tipo === 'P' ? 'text-[10px] font-mono font-bold tabular-nums min-w-[1.5rem] text-center' : 'text-[9px] font-bold uppercase tracking-wide'} px-1.5 py-0.5 rounded border ${COR_BLOCO[tipo] ?? COR_BLOCO.P}`}
              title={tipo === 'P' ? `parágrafo P#${rotulo}` : tipo}
            >
              {rotulo}
            </span>
            <div className={`flex-1 min-w-0 ${classeTexto(tipo)}`}>
              {texto || <span className="text-slate-400 dark:text-slate-500 italic">(vazio)</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
